import { showToast } from '#root/components/GlobalToaster';
import { OFFLINE_MESSAGES } from '#root/utils/offline';
import { useCallback, useEffect, useState } from 'react';
import { useBlocker, useLocation, useNavigate } from 'react-router-dom';

export class OfflineNavigationService {
    private static instance: OfflineNavigationService | null = null;
    private onlineDebounceTimeout: number | null = null;
    private offlineDebounceTimeout: number | null = null;
    private hasShownOfflineWarning: boolean = false;
    private isOffline: boolean = !navigator.onLine;
    private listeners: Set<(isOffline: boolean) => void> = new Set();

    private constructor() {
        if (OfflineNavigationService.instance) {
            throw new Error('Use OfflineNavigationService.getInstance()');
        }
        window.addEventListener('online', this.handleOnline);
        window.addEventListener('offline', this.handleOffline);

        window.addEventListener('unload', () => {
            if (this.onlineDebounceTimeout) {
                window.clearTimeout(this.onlineDebounceTimeout);
            }
            if (this.offlineDebounceTimeout) {
                window.clearTimeout(this.offlineDebounceTimeout);
            }
        });
    }

    public static getInstance(): OfflineNavigationService {
        if (!OfflineNavigationService.instance) {
            OfflineNavigationService.instance = new OfflineNavigationService();
        }
        return OfflineNavigationService.instance;
    }

    private notifyListeners() {
        this.listeners.forEach(listener => listener(this.isOffline));
    }

    public subscribe(listener: (isOffline: boolean) => void) {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    }

    private handleOnline = () => {
        if (this.onlineDebounceTimeout) {
            window.clearTimeout(this.onlineDebounceTimeout);
        }
        if (this.offlineDebounceTimeout) {
            window.clearTimeout(this.offlineDebounceTimeout);
            this.offlineDebounceTimeout = null;
        }

        this.isOffline = false;
        this.notifyListeners();

        this.onlineDebounceTimeout = window.setTimeout(() => {
            showToast(OFFLINE_MESSAGES.BACK_ONLINE, 'info');
            this.onlineDebounceTimeout = null;
            this.hasShownOfflineWarning = false;
        }, 1000);
    };

    private handleOffline = () => {
        if (this.offlineDebounceTimeout) {
            window.clearTimeout(this.offlineDebounceTimeout);
        }
        if (this.onlineDebounceTimeout) {
            window.clearTimeout(this.onlineDebounceTimeout);
            this.onlineDebounceTimeout = null;
        }

        this.isOffline = true;
        this.notifyListeners();

        this.offlineDebounceTimeout = window.setTimeout(() => {
            if (!this.hasShownOfflineWarning) {
                //showToast(OFFLINE_MESSAGES.LIMITED_FUNCTIONALITY, 'warning');
                this.hasShownOfflineWarning = true;
            }
            this.offlineDebounceTimeout = null;
        }, 1000);
    };

    public getIsOffline(): boolean {
        return this.isOffline;
    }
}

export const offlineNavigationService = OfflineNavigationService.getInstance();

export const usePreventNavigation = (shouldBlock: boolean) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [isOffline, setIsOffline] = useState(offlineNavigationService.getIsOffline());

    // Subscribe to offline state changes
    useEffect(() => {
        return offlineNavigationService.subscribe(setIsOffline);
    }, []);

    const shouldPreventNavigation = shouldBlock || isOffline;

    // Handle in-app navigation via React Router
    const blocker = useBlocker(shouldPreventNavigation);

    const handleBlockedNavigation = useCallback(() => {
        if (blocker.state === "blocked") {
            const proceed = window.confirm(OFFLINE_MESSAGES.NAVIGATION_WARNING);
            if (proceed) {
                blocker.proceed();
            } else {
                blocker.reset();
            }
        }
    }, [blocker]);

    // Handle the blocked navigation from React Router
    useEffect(() => {
        handleBlockedNavigation();
    }, [blocker.state, handleBlockedNavigation]);

    // Handle browser/tab close
    useEffect(() => {
        if (!shouldPreventNavigation) return;

        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = OFFLINE_MESSAGES.NAVIGATION_WARNING;
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [shouldPreventNavigation]);

    // Handle navigation via history API
    useEffect(() => {
        if (!shouldPreventNavigation) return;

        const handlePopState = (event: PopStateEvent) => {
            if (window.confirm(OFFLINE_MESSAGES.NAVIGATION_WARNING)) {
                return;
            }
            
            // Prevent navigation by pushing the current path back
            event.preventDefault();
            navigate(location.pathname, { replace: true });
        };

        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [shouldPreventNavigation, location.pathname, navigate]);
};
